import type { DealFieldCatalogue } from '@/services/dealFile'

/**
 * The intake Field Mapping, one row per deal field.
 *
 * The intake record keys its mapping by the parser's own names, camelCased by
 * the ingestion service (`borrowingEntity`, `termMonths`). The Documents tab
 * printed those keys verbatim, and `term` / `termMonths` (the stated "10
 * years" and the months derived from it) as two rows (nd3-29). Rows here are
 * named by the catalogue label of the field each key fills — the same rows
 * the dossier PDF prints — with every source that fed the field.
 */
export type IntakeMappingRow = { label: string; sources: string[] }

type Evidence = { sourceDoc?: string | null; cellRef?: string | null } | null | undefined

const squash = (key: string) => key.replace(/_/g, '').toLowerCase()

// Keys `toCamelCase` renames outright rather than camel-casing (utils/transform
// KEY_MAP): the mapping's `property_name` arrives as `property`, `asset_type`
// as `type`, `maturity_date` as `maturity`.
const RENAMED_BY_TRANSFORM: Record<string, string> = {
  property: 'property_name',
  type: 'asset_type',
  maturity: 'maturity_date',
}

function humanize(key: string): string {
  return key
    .replace(/[_-]+/g, ' ')
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .trim()
    .split(/\s+/)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(' ')
}

export function intakeMappingRows(
  mapping: Record<string, Evidence>,
  catalogue: DealFieldCatalogue | undefined,
): IntakeMappingRow[] {
  const aliases = new Map(
    Object.entries(catalogue?.intake_aliases ?? {}).map(([key, target]) => [squash(key), target]),
  )
  const labels = new Map((catalogue?.fields ?? []).map((f) => [squash(f.name), f.label]))
  const rows = new Map<string, IntakeMappingRow>()
  for (const [raw, meta] of Object.entries(mapping)) {
    const key = RENAMED_BY_TRANSFORM[raw] ?? raw
    const target = aliases.get(squash(key)) ?? key
    const label = labels.get(squash(target)) ?? labels.get(squash(key)) ?? humanize(key)
    const row = rows.get(label) ?? { label, sources: [] }
    const source = [meta?.sourceDoc, meta?.cellRef].filter(Boolean).join(' · ')
    if (source && !row.sources.includes(source)) row.sources.push(source)
    rows.set(label, row)
  }
  return [...rows.values()].sort((a, b) => a.label.localeCompare(b.label))
}
